<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge, type BadgeVariants } from '@/components/ui/badge';
import { Button, type ButtonVariants } from '@/components/ui/button';
import type { CarouselApi } from '@/components/ui/carousel';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@/components/ui/carousel';

export interface Item {
    id: string | number;
    image: string;
    badge?: {
        label: string;
        color: BadgeVariants['color'];
    };
    title: string;
    description?: string;
    buttons: {
        label: string;
        url: string;
    }[];
}

const props = defineProps<{
    items: Item[];
}>();

const activeValue = ref<string>(props.items[0]?.id?.toString() ?? '');
const carouselRef = ref<InstanceType<typeof Carousel> | null>(null);
const carouselApi = ref<CarouselApi | undefined>();
// Flag to prevent circular updates between carousel and accordion
const isUpdatingFromCarousel = ref(false);

onMounted(() => {
    nextTick(() => {
        carouselApi.value = carouselRef.value?.carouselApi;

        if (!carouselApi.value) return;

        carouselApi.value.on('select', () => {
            const index = carouselApi.value.selectedSnap();

            const item = props.items[index];

            if (!item) return;

            isUpdatingFromCarousel.value = true;
            activeValue.value = item.id.toString();

            nextTick(() => {
                isUpdatingFromCarousel.value = false;
            });
        });
    });
});

// Watch for accordion changes and update carousel
watch(activeValue, (value) => {
    // Skip if the change came from carousel itself
    if (isUpdatingFromCarousel.value) return;

    const index = props.items.findIndex((item) => item.id.toString() === value);
    if (index !== -1 && carouselApi.value) {
        carouselApi.value.goTo(index);
    }
});

// Handle dot indicator click
function handleDotClick(index: number) {
    const item = props.items[index];
    if (item && carouselApi.value) {
        activeValue.value = item.id.toString();
        carouselApi.value.goTo(index);
    }
}
</script>

<template>
    <div
        class="bg-muted divide-border flex min-h-120 flex-col justify-items-stretch divide-x overflow-hidden rounded border *:w-full md:flex-row *:md:w-1/2"
    >
        <!-- Carousel Section (Left) -->
        <Carousel
            ref="carouselRef"
            class="relative"
        >
            <CarouselContent class="h-64 md:h-full">
                <CarouselItem
                    v-for="item in items"
                    :key="item.id"
                    class="relative"
                >
                    <div class="absolute inset-0">
                        <img
                            :src="item.image"
                            :alt="item.title"
                            class="size-full object-cover object-center"
                        />
                        <div
                            class="absolute inset-0 bg-linear-to-b from-transparent from-70% to-black/10"
                        />
                    </div>
                </CarouselItem>
            </CarouselContent>

            <!-- Dot Indicators -->
            <div
                class="absolute inset-x-0 bottom-5 flex items-center justify-center gap-2"
            >
                <button
                    v-for="(item, index) in items"
                    :key="item.id"
                    type="button"
                    class="size-2 cursor-pointer rounded-full transition-colors"
                    :class="
                        item.id.toString() === activeValue
                            ? 'bg-primary'
                            : 'bg-white'
                    "
                    @click="handleDotClick(index)"
                >
                    <span class="sr-only">Zu Seite {{ index + 1 }}</span>
                </button>
            </div>
        </Carousel>

        <!-- Accordion Section (Right) -->
        <Accordion
            v-model="activeValue"
            type="single"
            class="flex flex-col"
        >
            <AccordionItem
                v-for="item in items"
                :key="item.id"
                :value="item.id.toString()"
                class="grid grid-cols-1 data-[state=closed]:grid-rows-[auto_0] data-[state=open]:grid-rows-[0_1fr] data-[state=open]:border-b-0 data-[state=open]:border-b-transparent data-[state=open]:shadow-sm"
            >
                <AccordionTrigger
                    class="hover:bg-muted/50 text-muted-foreground px-6 py-5 leading-6 font-semibold hover:no-underline"
                >
                    {{ item.title }}
                </AccordionTrigger>
                <AccordionContent
                    class="border-border bg-card flex h-full flex-1 grow flex-col gap-6 border-b px-6 py-5"
                >
                    <div class="flex flex-col gap-1.5">
                        <Badge
                            v-if="item.badge"
                            variant="secondary"
                            :color="item.badge.color"
                        >
                            {{ item.badge.label }}
                        </Badge>
                        <h4
                            class="flex-1 text-xl leading-6 font-semibold tracking-tight"
                        >
                            {{ item.title }}
                        </h4>
                    </div>
                    <p
                        v-if="item.description"
                        class="max-w-120 text-base leading-6 tracking-tight"
                    >
                        {{ item.description }}
                    </p>
                    <div
                        v-if="item.buttons?.length > 0"
                        class="flex flex-wrap items-start gap-3"
                    >
                        <Button
                            v-for="(button, index) in item.buttons"
                            as="a"
                            :href="button.url"
                            :variant="index === 0 ? 'default' : 'outline'"
                        >
                            {{ button.label }}
                        </Button>
                    </div>
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    </div>
</template>
